import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Box,
  Typography,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import axiosInstance from '../api/axiosConfig';
import { useSnackbar } from './common/SnackbarProvider';

export type HouseholdType = 'member' | 'non-member';

interface HouseholdDialogProps {
  open: boolean;
  /** 'member' builds a Member hierarchy; 'non-member' builds NonMemberFamily rows. */
  type: HouseholdType;
  onClose: () => void;
  /** Called after a successful create so the parent can refetch its list. */
  onCreated: () => void;
}

interface Dependent {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  relationship: string;
}

// Member dependents map to RelationshipTypeEnum; non-member family to
// NonMemberRelationshipEnum (which also offers spouse/partner since a non-member
// "partner" is just another family row).
const MEMBER_DEP_RELATIONSHIPS = ['CHILD', 'SIBLING', 'PARENT', 'OTHER'];
const NON_MEMBER_RELATIONSHIPS = ['CHILD', 'SPOUSE', 'PARTNER', 'PARENT', 'SIBLING', 'OTHER'];

const emptyDependent = (): Dependent => ({
  first_name: '',
  last_name: '',
  date_of_birth: '',
  relationship: 'CHILD',
});

const emptyPrimary = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  mobile_phone: '',
  date_of_birth: '',
  member_number: '',
  membership_tier_id: '', // set from the real tiers loaded on open; none if unconfigured
  address_line1: '',
  city: '',
  state: '',
  postal_code: '',
};

const emptyPartner = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  mobile_phone: '',
  date_of_birth: '',
  relationship: 'SPOUSE',
  member_number: '',
};

const titleCase = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();

const HouseholdDialog: React.FC<HouseholdDialogProps> = ({ open, type, onClose, onCreated }) => {
  const snackbar = useSnackbar();
  const isMember = type === 'member';

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [primary, setPrimary] = useState({ ...emptyPrimary });
  const [partnerEnabled, setPartnerEnabled] = useState(false);
  const [partner, setPartner] = useState({ ...emptyPartner });
  const [dependents, setDependents] = useState<Dependent[]>([]);
  const [tiers, setTiers] = useState<Array<{ id: number; tier_name: string }>>([]);

  // Load the REAL membership tiers when the dialog opens (member households only).
  // The DB may have none configured yet (payments deferred) — then we just create
  // the household with no tier (the API only validates a tier when one is sent).
  useEffect(() => {
    if (!open || !isMember) return;
    let active = true;
    axiosInstance
      .get('/membership-tiers/')
      .then((resp) => {
        if (!active) return;
        const list = (resp.data?.data || []).map((t: any) => ({ id: t.id, tier_name: t.tier_name }));
        setTiers(list);
        setPrimary((p) =>
          p.membership_tier_id ? p : { ...p, membership_tier_id: list[0] ? String(list[0].id) : '' }
        );
      })
      .catch(() => {
        if (active) setTiers([]);
      });
    return () => {
      active = false;
    };
  }, [open, isMember]);

  const resetAll = () => {
    setPrimary({ ...emptyPrimary });
    setPartnerEnabled(false);
    setPartner({ ...emptyPartner });
    setDependents([]);
    setError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    resetAll();
    onClose();
  };

  const addDependent = () => setDependents((d) => [...d, emptyDependent()]);
  const updateDependent = (i: number, patch: Partial<Dependent>) =>
    setDependents((d) => d.map((dep, idx) => (idx === i ? { ...dep, ...patch } : dep)));
  const removeDependent = (i: number) => setDependents((d) => d.filter((_, idx) => idx !== i));

  const validate = (): string | null => {
    if (!primary.first_name.trim() || !primary.last_name.trim()) {
      return 'Primary first and last name are required.';
    }
    if (!primary.email.trim() || !primary.password) {
      return 'The primary account holder needs an email and password.';
    }
    if (isMember && partnerEnabled) {
      if (!partner.first_name.trim() || !partner.last_name.trim()) {
        return 'Partner first and last name are required.';
      }
      if (!partner.email.trim() || !partner.password) {
        return 'A partner gets their own login, so they need an email and password.';
      }
    }
    for (let i = 0; i < dependents.length; i++) {
      const dep = dependents[i];
      const started = dep.first_name.trim() || dep.last_name.trim() || dep.date_of_birth;
      if (started && (!dep.first_name.trim() || !dep.last_name.trim())) {
        return `${isMember ? 'Child' : 'Family member'} ${i + 1}: first and last name are required.`;
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setSubmitting(true);

    // Only send rows that actually have a name.
    const filled = dependents.filter((d) => d.first_name.trim() && d.last_name.trim());

    const primaryCommon = {
      first_name: primary.first_name.trim(),
      last_name: primary.last_name.trim(),
      email: primary.email.trim(),
      password: primary.password,
      mobile_phone: primary.mobile_phone.trim() || undefined,
      date_of_birth: primary.date_of_birth || undefined,
      address_line1: primary.address_line1.trim() || undefined,
      city: primary.city.trim() || undefined,
      state: primary.state.trim() || undefined,
      postal_code: primary.postal_code.trim() || undefined,
    };

    let endpoint: string;
    let payload: any;
    if (isMember) {
      endpoint = '/members/household';
      payload = {
        primary: {
          ...primaryCommon,
          member_number: primary.member_number.trim() || undefined,
          membership_tier_id: primary.membership_tier_id
            ? parseInt(primary.membership_tier_id, 10)
            : undefined,
        },
        partner: partnerEnabled
          ? {
              first_name: partner.first_name.trim(),
              last_name: partner.last_name.trim(),
              email: partner.email.trim(),
              password: partner.password,
              mobile_phone: partner.mobile_phone.trim() || undefined,
              date_of_birth: partner.date_of_birth || undefined,
              relationship_to_primary: partner.relationship,
              member_number: partner.member_number.trim() || undefined,
            }
          : null,
        dependents: filled.map((d) => ({
          first_name: d.first_name.trim(),
          last_name: d.last_name.trim(),
          date_of_birth: d.date_of_birth || undefined,
          relationship_to_primary: d.relationship || 'CHILD',
          // kids' account numbers auto-assign server-side ({primary}-DPnn); a staff
          // override happens on the member edit screen, not at create time.
        })),
      };
    } else {
      endpoint = '/non-members/household';
      payload = {
        primary: { ...primaryCommon, acquisition_source: 'admin_dashboard' },
        family: filled.map((d) => ({
          first_name: d.first_name.trim(),
          last_name: d.last_name.trim(),
          date_of_birth: d.date_of_birth || undefined,
          relationship_type: d.relationship || 'CHILD',
        })),
      };
    }

    try {
      const resp = await axiosInstance.post(endpoint, payload);
      const count = resp.data?.data?.count ?? 1;
      snackbar.success(`Household created — ${count} ${count === 1 ? 'person' : 'people'} added.`);
      resetAll();
      onCreated();
      onClose();
    } catch (err: any) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to create household';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const relationshipOptions = isMember ? MEMBER_DEP_RELATIONSHIPS : NON_MEMBER_RELATIONSHIPS;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add {isMember ? 'Member' : 'Non-Member'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            Add the whole household at once — the primary account holder
            {isMember ? ', an optional partner,' : ''} and any{' '}
            {isMember ? 'children' : 'family members'}. Everyone is created together and linked;
            nothing is saved if any entry is invalid.
          </Typography>

          {/* Primary */}
          <Divider textAlign="left">
            <Typography variant="overline">Primary account holder</Typography>
          </Divider>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              required
              label="First Name"
              value={primary.first_name}
              onChange={(e) => setPrimary({ ...primary, first_name: e.target.value })}
            />
            <TextField
              fullWidth
              required
              label="Last Name"
              value={primary.last_name}
              onChange={(e) => setPrimary({ ...primary, last_name: e.target.value })}
            />
          </Box>
          <TextField
            fullWidth
            required
            type="email"
            label="Email"
            value={primary.email}
            onChange={(e) => setPrimary({ ...primary, email: e.target.value })}
          />
          <TextField
            fullWidth
            required
            type="password"
            label="Password"
            value={primary.password}
            onChange={(e) => setPrimary({ ...primary, password: e.target.value })}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="Mobile Phone"
              value={primary.mobile_phone}
              onChange={(e) => setPrimary({ ...primary, mobile_phone: e.target.value })}
            />
            <TextField
              fullWidth
              type="date"
              label="Date of Birth"
              InputLabelProps={{ shrink: true }}
              value={primary.date_of_birth}
              onChange={(e) => setPrimary({ ...primary, date_of_birth: e.target.value })}
            />
          </Box>
          {isMember && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {tiers.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>Membership Tier</InputLabel>
                  <Select
                    label="Membership Tier"
                    value={primary.membership_tier_id}
                    onChange={(e) => setPrimary({ ...primary, membership_tier_id: e.target.value })}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {tiers.map((t) => (
                      <MenuItem key={t.id} value={String(t.id)}>
                        {t.tier_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField
                fullWidth
                label="Member Number"
                placeholder="Auto-generates if blank"
                value={primary.member_number}
                onChange={(e) => setPrimary({ ...primary, member_number: e.target.value })}
              />
            </Box>
          )}
          <TextField
            fullWidth
            label="Address"
            value={primary.address_line1}
            onChange={(e) => setPrimary({ ...primary, address_line1: e.target.value })}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="City"
              value={primary.city}
              onChange={(e) => setPrimary({ ...primary, city: e.target.value })}
            />
            <TextField
              sx={{ width: 110 }}
              label="State"
              value={primary.state}
              onChange={(e) => setPrimary({ ...primary, state: e.target.value })}
            />
            <TextField
              sx={{ width: 150 }}
              label="Postal Code"
              value={primary.postal_code}
              onChange={(e) => setPrimary({ ...primary, postal_code: e.target.value })}
            />
          </Box>

          {/* Partner (members only — a SECONDARY member gets their own login) */}
          {isMember && (
            <>
              <Divider textAlign="left">
                <Typography variant="overline">Partner / spouse</Typography>
              </Divider>
              <FormControlLabel
                control={
                  <Switch
                    checked={partnerEnabled}
                    onChange={(e) => setPartnerEnabled(e.target.checked)}
                  />
                }
                label="Add a partner / spouse (gets their own login)"
              />
              {partnerEnabled && (
                <>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      required
                      label="First Name"
                      value={partner.first_name}
                      onChange={(e) => setPartner({ ...partner, first_name: e.target.value })}
                    />
                    <TextField
                      fullWidth
                      required
                      label="Last Name"
                      value={partner.last_name}
                      onChange={(e) => setPartner({ ...partner, last_name: e.target.value })}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    label="Partner's Member Number"
                    placeholder="Auto-generates if blank"
                    value={partner.member_number}
                    onChange={(e) => setPartner({ ...partner, member_number: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="Email"
                    value={partner.email}
                    onChange={(e) => setPartner({ ...partner, email: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    required
                    type="password"
                    label="Password"
                    value={partner.password}
                    onChange={(e) => setPartner({ ...partner, password: e.target.value })}
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Mobile Phone"
                      value={partner.mobile_phone}
                      onChange={(e) => setPartner({ ...partner, mobile_phone: e.target.value })}
                    />
                    <TextField
                      fullWidth
                      type="date"
                      label="Date of Birth"
                      InputLabelProps={{ shrink: true }}
                      value={partner.date_of_birth}
                      onChange={(e) => setPartner({ ...partner, date_of_birth: e.target.value })}
                    />
                    <FormControl sx={{ minWidth: 130 }}>
                      <InputLabel>Relationship</InputLabel>
                      <Select
                        label="Relationship"
                        value={partner.relationship}
                        onChange={(e) => setPartner({ ...partner, relationship: e.target.value })}
                      >
                        <MenuItem value="SPOUSE">Spouse</MenuItem>
                        <MenuItem value="PARTNER">Partner</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </>
              )}
            </>
          )}

          {/* Dependents / family */}
          <Divider textAlign="left">
            <Typography variant="overline">{isMember ? 'Children' : 'Family members'}</Typography>
          </Divider>
          {dependents.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No {isMember ? 'children' : 'family members'} added yet.
            </Typography>
          )}
          {dependents.map((dep, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                alignItems: { xs: 'stretch', sm: 'flex-start' },
                // On mobile each child stacks into a bordered group so multiple
                // children stay visually distinct; inline (no border) on desktop.
                border: { xs: '1px solid', sm: 'none' },
                borderColor: 'divider',
                borderRadius: 1,
                p: { xs: 1.5, sm: 0 },
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="First Name"
                value={dep.first_name}
                onChange={(e) => updateDependent(i, { first_name: e.target.value })}
              />
              <TextField
                fullWidth
                size="small"
                label="Last Name"
                value={dep.last_name}
                onChange={(e) => updateDependent(i, { last_name: e.target.value })}
              />
              <TextField
                size="small"
                type="date"
                label="DOB"
                InputLabelProps={{ shrink: true }}
                value={dep.date_of_birth}
                onChange={(e) => updateDependent(i, { date_of_birth: e.target.value })}
                sx={{ minWidth: 150 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Relationship</InputLabel>
                <Select
                  label="Relationship"
                  value={dep.relationship}
                  onChange={(e) => updateDependent(i, { relationship: e.target.value })}
                >
                  {relationshipOptions.map((r) => (
                    <MenuItem key={r} value={r}>
                      {titleCase(r)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Remove">
                <IconButton
                  color="error"
                  onClick={() => removeDependent(i)}
                  sx={{ alignSelf: { xs: 'flex-end', sm: 'flex-start' }, mt: { sm: 0.5 } }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addDependent}
            variant="outlined"
            size="small"
            sx={{ alignSelf: 'flex-start' }}
          >
            Add {isMember ? 'child' : 'family member'}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HouseholdDialog;
